import React, { useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../../components/utils";
import { Input } from "../../../../components/shared/InputField";
import {
  CorporateActionParams,
  OptionsRepricingDetails,
  RepricingGrantPreviewDetail,
} from "../../../../types/CompanyProfile";
import { usePreviewOptionsRepricing } from "../../../../queries/onBoarding";
import { formatDisplayDate } from "../../../../utils/date";
import {
  formatCurrency,
  getCurrencyType,
} from "../../../../utils/currencyFormatter";

function OverridePriceCellRenderer({
  data,
}: {
  data: RepricingGrantPreviewDetail;
}) {
  const formik = useFormikContext<OptionsRepricingDetails>();
  const grantId = data?.id ?? data?.grantId;
  return (
    <Input
      type="number"
      step="0.01"
      min="0"
      className="w-28 h-7 text-sm"
      placeholder="Leave blank"
      value={formik.values.grantPriceOverrides?.[grantId]?.toString() ?? ""}
      onChange={(e) => {
        const current = { ...(formik.values.grantPriceOverrides || {}) };
        if (e.target.value === "") {
          delete current[grantId];
        } else {
          current[grantId] = Number(e.target.value);
        }
        formik.setFieldValue("grantPriceOverrides", current);
      }}
    />
  );
}

type Props = {
  savedCorporateActionId: string | undefined;
  setSavedCorporateActionId: (id: string) => void;
  previewGrants: RepricingGrantPreviewDetail[];
  setPreviewGrants: (grants: RepricingGrantPreviewDetail[]) => void;
  onStepChange: (step?: number) => void;
  onBackClick: () => void;
  readonly?: boolean;
  isLoading?: boolean;
};

export default function PreviewRepricingGrants({
  savedCorporateActionId,
  setSavedCorporateActionId,
  previewGrants,
  setPreviewGrants,
  onStepChange,
  onBackClick,
  readonly,
  isLoading,
}: Props) {
  const formik = useFormikContext<OptionsRepricingDetails>();
  const currencyType = getCurrencyType();
  const { mutate: previewRepricing } = usePreviewOptionsRepricing();

  function buildParams(values: OptionsRepricingDetails): CorporateActionParams {
    return {
      id: values.id,
      name: values.name,
      date: values.date,
      type: "OPTION_REPRICING",
      ratio: 1,
      roundingMethodology: "ROUND_DOWN",
      state: values.state,
      note: values.note,
      metaData: {
        filters: {
          planIds: values.filters?.planIds,
          employmentStatus: values.filters?.employmentStatus,
          employeeType: values.filters?.employmentType,
          grantState: values.filters?.grantState,
          dateOfGrantFrom: values.filters?.grantDateFrom,
          dateOfGrantTo: values.filters?.grantDateTo,
          grantIds: values.filters?.grantIds,
        },
        repricingDetails: {
          repricingType: values.repricingType,
          newStrikePrice: values.newStrikePrice,
          grantStrikePriceOverrides: values.grantPriceOverrides,
        },
      },
    };
  }

  function handleSaveAndPreview() {
    previewRepricing(buildParams(formik.values), {
      onSuccess: (data) => {
        setSavedCorporateActionId(data.corporateActionId);
        formik.setFieldValue("id", data.corporateActionId);
        setPreviewGrants(data.grantDetails || []);
        toast.success("Preview generated successfully");
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.errorRaw || "Error generating preview"
        );
      },
    });
  }

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Grant ID",
        field: "identifier",
        minWidth: 120,
        sortable: true,
      },
      {
        headerName: "Holder Name",
        field: "employeeName",
        minWidth: 160,
        sortable: true,
      },
      { headerName: "Plan", field: "planName", minWidth: 140, sortable: true },
      {
        headerName: "Grant Date",
        field: "dateOfGrant",
        minWidth: 120,
        sortable: true,
        filter: true,
        valueFormatter: (p) => (p.value ? formatDisplayDate(p.value) : ""),
      },
      {
        headerName: "Grant State",
        field: "grantState",
        minWidth: 120,
        sortable: true,
      },
      {
        headerName: "Current Strike Price",
        field: "currentStrikePrice",
        minWidth: 160,
        sortable: true,
        valueFormatter: (p) =>
          p.value != null
            ? formatCurrency(Number(p.value.toFixed(2)), currencyType || "")
            : "",
      },
      {
        headerName: "New Strike Price",
        field: "newStrikePrice",
        minWidth: 150,
        sortable: true,
        valueFormatter: (p) =>
          p.value != null
            ? formatCurrency(Number(p.value.toFixed(2)), currencyType || "")
            : "",
      },
      ...(!readonly
        ? [
            {
              headerName: "Override Price",
              minWidth: 150,
              cellRenderer: OverridePriceCellRenderer,
            },
          ]
        : []),
    ],
    [currencyType, readonly]
  );

  const vestingRows = useMemo(
    () =>
      formik.values.repricingType === "VESTING"
        ? previewGrants.flatMap((g) =>
            (g.vestings || []).map((v) => ({
              ...v,
              identifier: g.identifier,
              employeeName: g.employeeName,
            }))
          )
        : [],
    [previewGrants, formik.values.repricingType]
  );

  const vestingColDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Grant ID",
        field: "identifier",
        minWidth: 120,
        sortable: true,
      },
      {
        headerName: "Holder Name",
        field: "employeeName",
        minWidth: 160,
        sortable: true,
      },
      {
        headerName: "Vesting Date",
        field: "projectedVestingDate",
        minWidth: 130,
        sortable: true,
        filter: true,
        valueFormatter: (p) => (p.value ? formatDisplayDate(p.value) : ""),
      },
      {
        headerName: "Old Strike Price",
        field: "currentStrikePrice",
        minWidth: 150,
        sortable: true,
        valueFormatter: (p) =>
          p.value != null
            ? formatCurrency(Number(p.value.toFixed(2)), currencyType || "")
            : "",
      },
      {
        headerName: "New Strike Price",
        field: "newStrikePrice",
        minWidth: 150,
        sortable: true,
        valueFormatter: (p) =>
          p.value != null
            ? formatCurrency(Number(p.value.toFixed(2)), currencyType || "")
            : "",
      },
    ],
    [currencyType]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      flex: 1,
      resizable: true,
      minWidth: 100,
      filter: false,
      menuTabs: [],
    }),
    []
  );

  const isVestingType = formik.values.repricingType === "VESTING";
  const [activeTab, setActiveTab] = useState<"grants" | "vestings">("grants");

  const canProceed = !!savedCorporateActionId;

  return (
    <VStack className="w-full gap-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">
          Preview &amp; Overrides
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Generate a preview of affected grants. Optionally override the price
          for individual grants.
        </p>
      </div>

      <HStack className="items-center gap-4">
        {savedCorporateActionId && (
          <HStack className="items-center gap-1 text-green-600 text-sm hidden">
            <Icon icon="akar-icons:check" width={14} />
            <span>Draft saved</span>
          </HStack>
        )}
      </HStack>

      {previewGrants.length > 0 ? (
        <VStack className="gap-0">
          {isVestingType && (
            <HStack className="border-b border-gray-200 hidden">
              {(["grants", "vestings"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === tab
                      ? "border-orange-501 text-orange-501"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab === "grants"
                    ? `Grants (${previewGrants.length})`
                    : `Vestings (${vestingRows.length})`}
                </button>
              ))}
            </HStack>
          )}
          {(!isVestingType || activeTab === "grants") && (
            <Box
              style={{
                height: `${Math.max(
                  400,
                  Math.min(previewGrants.length + 3, 13) * 60
                )}px`,
              }}
              className="w-full ag-theme-material"
            >
              <AgGridReact
                rowData={previewGrants}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                animateRows
                suppressRowTransform
                rowClass="border-t border-dashed hover:bg-slate-50"
                overlayNoRowsTemplate='<span style="padding:10px">No grants found for the given filters</span>'
              />
            </Box>
          )}
          {isVestingType && activeTab === "vestings" && (
            <Box
              style={{
                height: `${Math.max(
                  400,
                  Math.min(vestingRows.length + 3, 13) * 60
                )}px`,
              }}
              className="w-full ag-theme-material"
            >
              <AgGridReact
                rowData={vestingRows}
                columnDefs={vestingColDefs}
                defaultColDef={defaultColDef}
                animateRows
                suppressRowTransform
                rowClass="border-t border-dashed hover:bg-slate-50"
                overlayNoRowsTemplate='<span style="padding:10px">No vesting details available</span>'
              />
            </Box>
          )}
        </VStack>
      ) : (
        <VStack className="py-12 items-center gap-2 border border-dashed border-gray-200 rounded-lg">
          <Icon
            icon="material-symbols:preview-outline"
            width={40}
            className="text-gray-300"
          />
          <p className="text-sm text-gray-400">
            Click &quot;Save &amp; Preview&quot; to see affected grants
          </p>
        </VStack>
      )}

      <HStack className="justify-between mt-4">
        <ButtonPrimary1
          className="h-8 px-4 py-1"
          onClick={onBackClick}
          type="button"
        >
          Back
        </ButtonPrimary1>
        {!readonly && (
          <ButtonPrimary
            className="h-8 px-4 py-1"
            disabled={!canProceed || isLoading}
            loading={isLoading}
            onClick={() => onStepChange()}
            type="button"
          >
            Next
          </ButtonPrimary>
        )}
      </HStack>
    </VStack>
  );
}
